import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  RefreshCw, CalendarCheck, Eye, EyeOff, Trash2,
  Loader2, CheckCircle2, XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "@/components/ui/sonner";
import PageHeader from "@/components/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import GoogleCalendarSync from "@/components/GoogleCalendarSync";
import ManualAppointmentPanel from "@/components/ManualAppointmentPanel";
import { useAuth } from "@/contexts/AuthContext";

const API = `${window.location.origin}/api`;

export default function AppuntamentiDuplicatiPage() {
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [duplicates, setDuplicates] = useState({ groups: [], total: 0 });
  const [_hiddenGroups, setHiddenGroups] = useState([]);
  const [hiddenKeys, setHiddenKeys] = useState(new Set());
  const [apptStatus, setApptStatus] = useState(null);
  const [apptRunning, setApptRunning] = useState(false);
  const [cancellationReports, setCancellationReports] = useState([]);
  const [autoCancelling, setAutoCancelling] = useState(false);

  const fetchDuplicates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/appointments/duplicates`, { withCredentials: true });
      setDuplicates(data);
    } catch (err) { console.error("fetchDuplicates:", err); }
  }, []);

  const fetchHidden = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/appointments/hidden`, { withCredentials: true });
      const items = data.items || [];
      setHiddenGroups(items);
      setHiddenKeys(new Set(items.map((h) => h.group_key)));
    } catch (err) { console.error("fetchHidden:", err); }
  }, []);

  const fetchApptStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/appointments/check-status`, { withCredentials: true });
      setApptStatus(data.status);
      setApptRunning(data.running);
    } catch (err) { console.error("fetchApptStatus:", err); }
  }, []);

  const fetchCancellationReports = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/appointments/cancellation-reports`, { withCredentials: true });
      setCancellationReports(data.reports || []);
    } catch (err) { console.error("fetchCancellationReports:", err); }
  }, []);

  useEffect(() => {
    fetchDuplicates();
    fetchHidden();
    fetchApptStatus();
    fetchCancellationReports();
  }, [fetchDuplicates, fetchHidden, fetchApptStatus, fetchCancellationReports]);

  const checkDuplicates = async () => {
    try {
      await axios.post(`${API}/appointments/check-duplicates`, {}, { withCredentials: true });
      setApptRunning(true);
      toast.success("Controllo appuntamenti avviato");
      const poll = setInterval(async () => {
        const { data } = await axios.get(`${API}/appointments/check-status`, { withCredentials: true });
        setApptStatus(data.status);
        setApptRunning(data.running);
        if (!data.running) {
          clearInterval(poll);
          fetchDuplicates();
          fetchCancellationReports();
        }
      }, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel controllo appuntamenti");
    }
  };

  const cancelAppointment = async (eventId) => {
    try {
      const { data } = await axios.put(`${API}/appointments/${eventId}/cancel`, {}, { withCredentials: true });
      if (data?.queued) {
        toast.success("Cancellazione registrata — GHL lento, sincronizzazione in corso", {
          duration: 5000,
          description: "L'operazione verrà ritentata automaticamente entro 60 secondi.",
        });
      } else {
        toast.success("Appuntamento cancellato");
      }
      fetchDuplicates();
    } catch (err) {
      console.error("cancelAppointment:", err);
      toast.error("Errore nella cancellazione");
    }
  };

  const autoCancelDuplicates = async () => {
    if (!window.confirm(`Sei sicuro? Verranno cancellati automaticamente ${duplicates?.groups?.length || 0} gruppi di duplicati (viene mantenuto il primo appuntamento di ogni gruppo).`)) return;
    setAutoCancelling(true);
    try {
      const { data } = await axios.post(`${API}/appointments/auto-cancel-duplicates`, {}, { withCredentials: true, timeout: 300000 });
      toast.success(data.message);
      if (data.failed > 0) toast.warning(`${data.failed} appuntamenti non cancellati (errore GHL)`);
      fetchDuplicates();
      fetchCancellationReports();
    } catch (err) {
      console.error("autoCancelDuplicates:", err);
      toast.error(err.response?.data?.detail || "Errore nella cancellazione automatica");
    } finally {
      setAutoCancelling(false);
    }
  };

  const hideGroup = async (groupKey) => {
    try {
      await axios.post(`${API}/appointments/${groupKey}/hide`, {}, { withCredentials: true });
      toast.success("Gruppo nascosto");
      fetchHidden();
    } catch {
      toast.error("Errore nel nascondere il gruppo");
    }
  };

  const unhideGroup = async (groupKey) => {
    try {
      await axios.post(`${API}/appointments/${groupKey}/unhide`, {}, { withCredentials: true });
      toast.success("Gruppo ripristinato");
      fetchHidden();
    } catch {
      toast.error("Errore nel ripristinare il gruppo");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 bg-grid-light pb-12 pt-[calc(7px+3.5rem+13px)]">
      <Toaster position="top-right" theme="light" />
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <PageHeader
          title="Calendario"
          subtitle="Confronto Google Calendar ↔ GHL e pulizia dei duplicati"
          actions={
            <button
              type="button"
              onClick={checkDuplicates}
              disabled={apptRunning}
              data-testid="check-duplicates-btn"
              title={apptRunning ? "Controllo in corso…" : "Avvia controllo"}
              aria-label="Avvia controllo"
              className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-colors backdrop-blur-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {apptRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
          }
        />

        {/* Sezione 1: Confronto Google Calendar ↔ GHL */}
        <div className="-mt-8 relative z-10">
          <GoogleCalendarSync isAdmin={isAdmin} />
        </div>

        {/* Sezione 1.5: Inserimento manuale appuntamento (admin only) */}
        {isAdmin && <ManualAppointmentPanel />}

        {/* Sezione 2: Pulizia appuntamenti duplicati */}
        <div className="bg-white rounded-3xl shadow-md p-6 space-y-6 relative z-10">
          <div className="border-b border-slate-100 pb-3 mb-2">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <CalendarCheck className="w-5 h-5 text-amber-500" />
              Appuntamenti Duplicati in GHL
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Gruppi con stessa data, ora e paziente — controlla e cancella i doppioni.
            </p>
          </div>
          {/* Action bar */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              {duplicates.groups.length > 0 && (
                <Button
                  variant="destructive"
                  onClick={autoCancelDuplicates}
                  disabled={autoCancelling}
                  className="rounded-xl"
                  data-testid="auto-cancel-all-btn"
                >
                  {autoCancelling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  {autoCancelling ? "Cancellazione in corso…" : "Cancella tutti i duplicati"}
                </Button>
              )}
              {apptStatus && (
                <div className={`flex items-center gap-2 text-sm ${apptStatus.status === "completed" ? "text-green-600" : apptStatus.status === "error" ? "text-red-500" : "text-blue-500"}`}>
                  {apptStatus.status === "completed" ? <CheckCircle2 className="w-4 h-4" /> :
                   apptStatus.status === "running" ? <Loader2 className="w-4 h-4 animate-spin" /> :
                   <XCircle className="w-4 h-4" />}
                  <span>{apptStatus.message}</span>
                </div>
              )}
            </div>
          </div>

          {/* Groups list */}
          {duplicates.groups.length === 0 ? null : (
            <div className="space-y-4">
              {duplicates.groups.map((group) => {
                const dayNames = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
                const d = new Date(group.date + "T00:00:00");
                const dayName = dayNames[d.getDay()];
                const isHidden = hiddenKeys.has(group.group_key);
                return (
                  <div
                    key={group.group_key}
                    className="rounded-2xl border border-amber-200 overflow-hidden"
                    data-testid={`dup-group-${group.group_key}`}
                  >
                    <div
                      className={`bg-amber-50 flex items-center justify-between px-4 ${isHidden ? "py-1.5" : "py-3"}`}
                    >
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className={`font-semibold text-amber-800 ${isHidden ? "text-sm opacity-70" : ""}`}>
                          {dayName} {new Date(group.date).toLocaleDateString("it-IT")}
                        </span>
                        <span className={`text-amber-700 ${isHidden ? "text-sm opacity-70" : ""}`}>{group.time}</span>
                        <span className={`font-medium text-amber-600 ${isHidden ? "text-sm opacity-70" : ""}`}>{group.contact_name}</span>
                        <Badge className="text-xs bg-amber-200 text-amber-800">
                          {group.events.length}x
                        </Badge>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => isHidden ? unhideGroup(group.group_key) : hideGroup(group.group_key)}
                              className={`text-amber-500 hover:text-amber-700 hover:bg-amber-100 ${isHidden ? "h-6 w-6" : "h-8 w-8"}`}
                              data-testid={`toggle-hide-${group.group_key}`}
                            >
                              {isHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {isHidden ? "Mostra gruppo" : "Nascondi gruppo"}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    {!isHidden && group.events.map((ev) => (
                      <div key={ev.event_id} className="px-4 py-2.5 border-t border-amber-100 flex items-center justify-between gap-3 bg-white">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Badge variant="outline" className="text-xs shrink-0">{ev.calendar_name || group.calendar_name}</Badge>
                          {ev.notes && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-xs text-slate-500 truncate max-w-[200px] cursor-help">{ev.notes}</span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-sm whitespace-pre-wrap">{ev.notes}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => cancelAppointment(ev.event_id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg shrink-0"
                          data-testid={`cancel-${ev.event_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Cancella
                        </Button>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {/* Storico Cancellazioni Automatiche */}
          {cancellationReports.length > 0 && (
            <div className="pt-4 border-t border-slate-100" data-testid="cancellation-reports">
              <h3 className="font-semibold text-slate-700 mb-4">Storico Cancellazioni Automatiche</h3>
              <div className="space-y-3">
                {cancellationReports.map((report) => (
                  <details key={report.cancelled_at} className="rounded-xl border border-slate-200 overflow-hidden">
                    <summary className="cursor-pointer bg-slate-50 px-4 py-3 flex items-center justify-between text-sm hover:bg-slate-100">
                      <div className="flex items-center gap-3 flex-wrap">
                        <CalendarCheck className="w-4 h-4 text-green-500" />
                        <span className="font-medium text-slate-700">
                          {new Date(report.cancelled_at).toLocaleString("it-IT")}
                        </span>
                        <Badge className="bg-green-100 text-green-700 text-xs">{report.total_cancelled} cancellati</Badge>
                        {report.total_failed > 0 && (
                          <Badge className="bg-red-100 text-red-700 text-xs">{report.total_failed} falliti</Badge>
                        )}
                        <Badge variant="outline" className="text-xs">{report.groups_resolved} gruppi risolti</Badge>
                      </div>
                    </summary>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead>Data</TableHead>
                            <TableHead>Ora</TableHead>
                            <TableHead>Paziente</TableHead>
                            <TableHead>Calendario</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(report.details || []).map((d, di) => (
                            <TableRow key={`${d.date || ""}-${d.time || ""}-${d.contact_name || ""}-${di}`}>
                              <TableCell className="text-sm">{d.date ? new Date(d.date + "T00:00:00").toLocaleDateString("it-IT") : "–"}</TableCell>
                              <TableCell className="text-sm">{d.time || "–"}</TableCell>
                              <TableCell className="text-sm font-medium">{d.contact_name || "–"}</TableCell>
                              <TableCell><Badge variant="outline" className="text-xs">{d.calendar_name || "–"}</Badge></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
